import React from 'react';
import { CalendarOff } from 'lucide-react';

// Literal strings that AI extraction may store instead of a real date.
const INVALID_LITERALS = [
  'niet vermeld', 'onbekend', 'n.v.t', 'nvt', 'geen', 'onbepaald',
  'doorlopend', 'verlengd', 'tbd', 'pm',
];

/**
 * Robustly parse a date value coming from key_data.dates.
 * Returns a valid Date or null (for missing / unparseable / literal text).
 */
function parseDate(value) {
  if (!value || typeof value !== 'string') return null;
  const cleaned = value.trim();
  if (!cleaned) return null;
  const lower = cleaned.toLowerCase();
  if (INVALID_LITERALS.some((l) => lower.includes(l))) return null;
  const d = new Date(cleaned);
  if (Number.isNaN(d.getTime())) return null;
  const y = d.getFullYear();
  if (y < 1990 || y > 2100) return null; // reject absurd parses
  return d;
}

function formatNL(d) {
  return d.toLocaleDateString('nl-NL', { day: 'numeric', month: 'short', year: 'numeric' });
}

function Dot({ pct, cls }) {
  return (
    <div
      className={`absolute top-1/2 w-3 h-3 rounded-full border-2 border-white dark:border-[#0D1117] shadow-sm ${cls}`}
      style={{ left: `${pct}%`, transform: 'translate(-50%, -50%)' }}
    />
  );
}

function Legend({ cls, label, date, sub }) {
  return (
    <span className="flex items-center gap-1.5 text-[11px] text-slate-600 dark:text-[#8B949E]">
      <span className={`w-2 h-2 rounded-full flex-shrink-0 ${cls}`} />
      <span className="font-medium text-slate-700 dark:text-[#C9D1D9]">{label}:</span>
      <span className="tabular-nums">{date}</span>
      {sub && <span className="text-red-500 font-medium">({sub})</span>}
    </span>
  );
}

/**
 * Inline lifecycle timeline for a single contract entry.
 * Plots the full contract period (start -> indexatie -> opzegmoment -> end)
 * with a "vandaag" (today) marker. Degrades gracefully when dates are
 * missing, unparseable, or literal text like "Niet vermeld in document".
 */
export default function ContractTimeline({ entry }) {
  const dates = entry?.key_data?.dates || {};
  const start = parseDate(dates.start);
  const end = parseDate(dates.end);
  const opzeg = parseDate(dates.opzeg_voor);
  const indexatie = parseDate(dates.indexatie);
  const today = new Date();
  const opzegOverdue = opzeg ? opzeg.getTime() < today.getTime() : false;

  const known = [start, indexatie, opzeg, end].filter(Boolean);

  const wrapCls =
    'px-4 sm:px-6 py-5 bg-slate-50/70 dark:bg-[#0D1117] border-t border-slate-100 dark:border-[#21262D]';

  // Determine a drawable range. Prefer the full contract period (start -> end);
  // otherwise derive min/max from whatever valid dates exist.
  let rangeStart = null;
  let rangeEnd = null;
  if (start && end && end.getTime() > start.getTime()) {
    rangeStart = start;
    rangeEnd = end;
  } else if (known.length >= 2) {
    const times = known.map((d) => d.getTime());
    const lo = Math.min(...times);
    const hi = Math.max(...times);
    if (hi > lo) {
      rangeStart = new Date(lo);
      rangeEnd = new Date(hi);
    }
  }

  // Graceful fallback: not enough data to plot a proportional bar.
  if (!rangeStart || !rangeEnd) {
    return (
      <div className={wrapCls} data-testid={`contract-timeline-${entry.entry_id}`}>
        <div
          className="flex items-center gap-2 text-xs text-slate-400 dark:text-[#484F58]"
          data-testid="timeline-fallback"
        >
          <CalendarOff className="w-4 h-4 flex-shrink-0" />
          <span>Onvoldoende datums om een tijdlijn te tonen.</span>
        </div>
        {known.length === 1 && (
          <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1.5">
            {start && <Legend cls="bg-slate-400" label="Start" date={formatNL(start)} />}
            {indexatie && <Legend cls="bg-[#2E75B6]" label="Indexatie" date={formatNL(indexatie)} />}
            {opzeg && (
              <Legend
                cls={opzegOverdue ? 'bg-red-500' : 'bg-amber-500'}
                label="Opzegmoment"
                date={formatNL(opzeg)}
              />
            )}
            {end && <Legend cls="bg-red-500" label="Einde" date={formatNL(end)} />}
          </div>
        )}
      </div>
    );
  }

  const spanMs = rangeEnd.getTime() - rangeStart.getTime();
  const pct = (d) =>
    Math.min(100, Math.max(0, ((d.getTime() - rangeStart.getTime()) / spanMs) * 100));

  const pStart = start ? pct(start) : null;
  const pEnd = end ? pct(end) : null;
  const pIndex = indexatie ? pct(indexatie) : null;
  const pOpzeg = opzeg ? pct(opzeg) : null;
  const pToday = pct(today);

  const fillStart = pStart != null && pEnd != null ? Math.min(pStart, pEnd) : null;
  const fillEnd = pStart != null && pEnd != null ? Math.max(pStart, pEnd) : null;

  return (
    <div className={wrapCls} data-testid={`contract-timeline-${entry.entry_id}`}>
      {/* Proportional bar */}
      <div className="relative mb-4" style={{ paddingTop: 26 }}>
        {/* Vandaag label */}
        <div
          className="absolute top-0 z-20 -translate-x-1/2 flex flex-col items-center"
          style={{ left: `${pToday}%` }}
        >
          <span
            className="px-1.5 py-0.5 rounded bg-slate-900 dark:bg-white text-white dark:text-slate-900 text-[9px] font-semibold whitespace-nowrap"
            data-testid="timeline-today-label"
          >
            Vandaag
          </span>
        </div>

        {/* Track */}
        <div className="relative h-2.5 rounded-full bg-slate-200 dark:bg-[#21262D]">
          {/* Active contract period fill */}
          {fillStart != null && (
            <div
              className="absolute top-0 h-2.5 rounded-full bg-[#2E75B6]/25"
              style={{ left: `${fillStart}%`, width: `${Math.max(fillEnd - fillStart, 1.5)}%` }}
            />
          )}

          {/* Today vertical line */}
          <div
            className="absolute -top-1.5 -bottom-1.5 w-0.5 bg-slate-900 dark:bg-white z-10"
            style={{ left: `${pToday}%`, transform: 'translateX(-50%)' }}
          />

          {/* Markers */}
          {pStart != null && <Dot pct={pStart} cls="bg-slate-400" />}
          {pIndex != null && <Dot pct={pIndex} cls="bg-[#2E75B6]" />}
          {pOpzeg != null && <Dot pct={pOpzeg} cls={opzegOverdue ? 'bg-red-500' : 'bg-amber-500'} />}
          {pEnd != null && <Dot pct={pEnd} cls="bg-red-500" />}
        </div>
      </div>

      {/* Legend (also the readable date list for mobile) */}
      <div className="flex flex-wrap gap-x-5 gap-y-1.5" data-testid="timeline-legend">
        {start && <Legend cls="bg-slate-400" label="Start" date={formatNL(start)} />}
        {indexatie && <Legend cls="bg-[#2E75B6]" label="Indexatie" date={formatNL(indexatie)} />}
        {opzeg && (
          <Legend
            cls={opzegOverdue ? 'bg-red-500' : 'bg-amber-500'}
            label="Opzegmoment"
            date={formatNL(opzeg)}
            sub={opzegOverdue ? 'verlopen' : null}
          />
        )}
        {end && <Legend cls="bg-red-500" label="Einde" date={formatNL(end)} />}
      </div>
    </div>
  );
}
